import { useAuth } from '@eti/authentication';
import { useDirection, useTranslation } from '@eti/providers';
import { Envelope, Share } from '@phosphor-icons/react';
import { Inline, LinkButton, Stack } from '@etg/wings';
import type { MouseEvent } from 'react';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import { getTravelDocUrl } from '../../utils';
import ReceiptMailDialog from '../../../receipt/components/modals/ReceiptMailDialog';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { useScrollToElementViaQueryParams } from '../../../../common/custom-hooks/scrollToElementViaQueryParams';
import { SHOW_MAIL } from '../../constants/general';

const DocumentLinks = () => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { authenticatedAction, user } = useAuth();
  const { travelDocUrl } = useOrderDetails();
  const actualTravelDocUrl = getTravelDocUrl(travelDocUrl, isRTL);

  const {
    isOpen: isMailDialogOpen,
    handleClose: handleCloseMailDialog,
    handleToggle: handleToggleMailDialog,
  } = useToggle(false);

  const handleShowTravelDocument = (event: MouseEvent) => {
    event.preventDefault();
    authenticatedAction(() => window.open(actualTravelDocUrl, '_blank'));
  };

  useScrollToElementViaQueryParams('send-confirmation-button', SHOW_MAIL, handleToggleMailDialog);

  return (
    <>
      <Stack data-testid="documents-links">
        <Inline spacing={4}>
          <Share aria-hidden="true" mirrored={isRTL} size={20} />
          <a
            aria-label={`${t('OrderDetails.Itinerary.Overview.TravelDocumentButton.Label')}, ${t(
              'ScreenReader.Link.OpensInNewTab.AriaLabel.Text',
            )}`}
            data-testid="travel-document-button"
            href={actualTravelDocUrl}
            id="travel-document-button"
            onClick={() => handleShowTravelDocument}
            rel="noreferrer"
            target="_blank"
          >
            {t('OrderDetails.Itinerary.Overview.TravelDocumentButton.Label')}
          </a>
        </Inline>
        <Inline spacing={4}>
          <Envelope aria-hidden="true" mirrored={isRTL} size={20} />
          <LinkButton
            data-testid="send-confirmation-button"
            id="send-confirmation-button"
            onClick={handleToggleMailDialog}
          >
            {t('OrderDetails.Payment.Overview.SendConfirmationButton.Label')}
          </LinkButton>
        </Inline>
      </Stack>
      <ReceiptMailDialog
        customerEmail={user?.email}
        isOpen={isMailDialogOpen}
        onClose={handleCloseMailDialog}
        orderNumber={user?.sessionOrderNumber}
      />
    </>
  );
};

export default DocumentLinks;
